<template>
  <div class="container-fluid">
    <!-- Overview Statistics Card -->
    <b-card class="card-statistics mb-2">
      <b-card-header>
        <b-card-title>Broker Overview</b-card-title>
      </b-card-header>
      <b-card-body class="statistics-body">
        <b-row>
          <b-col
            md="3"
            sm="6"
            class="mb-2 mb-md-0"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-primary"
                size="45"
              >
                <feather-icon
                  size="21"
                  icon="BriefcaseIcon"
                />
              </b-avatar>
              <div class="ml-2">
                <h5 class="mb-0">
                  {{ totalBrokerJobs }}
                </h5>
                <small>Total Broker Jobs</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-2 mb-md-0"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-success"
                size="45"
              >
                <feather-icon
                  size="21"
                  icon="TrendingUpIcon"
                />
              </b-avatar>
              <div class="ml-2">
                <h5 class="mb-0">
                  {{ activeJobs }}
                </h5>
                <small>Active Listings</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-info"
                size="45"
              >
                <feather-icon
                  size="21"
                  icon="UsersIcon"
                />
              </b-avatar>
              <div class="ml-2">
                <h5 class="mb-0">
                  {{ matchedCandidates }}
                </h5>
                <small>Matched Candidates</small>
              </div>
            </div>
          </b-col>
          <b-col
            md="3"
            sm="6"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-warning"
                size="45"
              >
                <feather-icon
                  size="21"
                  icon="StarIcon"
                />
              </b-avatar>
              <div class="ml-2">
                <h5 class="mb-0">
                  {{ savedJobs }}
                </h5>
                <small>Saved Jobs</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Main Content Card -->
    <b-overlay :show="isLoading">
      <b-card no-body>
        <b-card-header>
          <b-card-title>Broker Job Listings</b-card-title>
          <div class="d-flex align-items-center">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="success"
              class="mr-1"
              @click="createNewJob"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              <span class="align-middle">Create New Job</span>
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-1"
              @click="refreshJobs"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50"
              />
              <span class="align-middle">Refresh Jobs</span>
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              @click="toggleFilters"
            >
              <feather-icon
                icon="FilterIcon"
                class="mr-50"
              />
              <span class="align-middle">{{ showFilters ? 'Hide' : 'Show' }} Filters</span>
            </b-button>
          </div>
        </b-card-header>

        <!-- Advanced Filters Section -->
        <b-collapse
          v-model="showFilters"
          class="mt-2"
        >
          <b-card-body class="border-top">
            <b-row>
              <b-col
                md="3"
                sm="6"
                class="mb-1"
              >
                <b-form-group
                  label="Status"
                  label-for="filter-status"
                >
                  <b-form-select
                    id="filter-status"
                    v-model="filters.status"
                    :options="statusOptions"
                    @change="applyFilters"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="3"
                sm="6"
                class="mb-1"
              >
                <b-form-group
                  label="Agency"
                  label-for="filter-agency"
                >
                  <b-form-input
                    id="filter-agency"
                    v-model="filters.agency"
                    placeholder="Search agency..."
                    @keyup.enter="applyFilters"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="3"
                sm="6"
                class="mb-1"
              >
                <b-form-group
                  label="Salary Range"
                  label-for="filter-salary"
                >
                  <b-form-select
                    id="filter-salary"
                    v-model="filters.salaryRange"
                    :options="salaryRangeOptions"
                    @change="applyFilters"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="3"
                sm="6"
                class="mb-1"
              >
                <b-form-group
                  label="Date Posted"
                  label-for="filter-date"
                >
                  <b-form-select
                    id="filter-date"
                    v-model="filters.datePosted"
                    :options="datePostedOptions"
                    @change="applyFilters"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-secondary"
                  size="sm"
                  @click="clearFilters"
                >
                  Clear Filters
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-collapse>

        <div class="w-100 d-flex flex-gap-sm">
          <b-button 
            pill 
            :variant="$isEmpty(serverParams.columnFilters['company_uen_no']) ? 'primary' : 'outline-primary'"
            @click="filterJobSource(null)"
          >All</b-button>

          <b-button 
            pill 
            :variant="serverParams.columnFilters['company_uen_no'] == $store.state.auth.ActiveUser.company_uen_no ? 'primary' : 'outline-primary'"
            @click="filterJobSource($store.state.auth.ActiveUser.company_uen_no)"
          >{{ $store.state.auth.ActiveUser.company_name }}</b-button>
        </div>

        <!-- VueGoodTable -->
        <vue-good-table
          class="mt-1"
          theme="polar-bear"
          mode="remote"
          :columns="brokerColumns"
          :rows="brokerJobs"
          :total-rows="totalRecords"
          :pagination-options="{
            enabled: true,
            mode: 'records',
            perPage: serverParams.perPage,
            position: 'bottom',
            perPageDropdown: [5, 10, 20, 50, 100],
            dropdownAllowAll: false,
            setCurrentPage: 1,
            nextLabel: 'next',
            prevLabel: 'prev',
            rowsPerPageLabel: 'Rows per page',
            ofLabel: 'of',
            pageLabel: 'page',
            allLabel: 'All',
          }"
          :search-options="{
            enabled: false,
          }"
          :select-options="{
            enabled: true,
            selectOnCheckboxOnly: true,
          }"
          :sort-options="{
            enabled: true,
          }"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
          @on-per-page-change="onPerPageChange"
          @on-selected-rows-change="onSelectionChange"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Position Column -->
            <span v-if="props.column.field === 'position'">
              <div class="d-flex flex-column">
                <span class="font-weight-bold">{{ props.row.position }}</span>
                <small class="text-muted">{{ props.row.company }}</small>
              </div>
            </span>

            <!-- Agency Column -->
            <span v-else-if="props.column.field === 'agency'">
              <div class="d-flex align-items-center">
                <b-avatar
                  :src="props.row.agencyLogo"
                  :text="props.row.agency.charAt(0)"
                  variant="light-primary"
                  size="32"
                  class="mr-1"
                />
                <span>{{ props.row.agency }}</span>
              </div>
            </span>

            <!-- Salary Column -->
            <span v-else-if="props.column.field === 'salary'">
              {{ $formatCurrency(props.row.salaryMin) }} - {{ $formatCurrency(props.row.salaryMax) }}
            </span>

            <!-- Matched Candidates Column -->
            <span v-else-if="props.column.field === 'candidates'">
              <b-badge
                :variant="props.row.candidates.interest_count > 0 ? 'light-success' : 'light-secondary'"
                pill
              >
                {{ props.row.candidates.interest_count }} Interests
              </b-badge>

              <b-badge
                :variant="props.row.candidates.matched_count > 0 ? 'light-success' : 'light-secondary'"
                pill
              >
                {{ props.row.candidates.matched_count }} Matched
              </b-badge>
            </span>

            <!-- Status Column -->
            <span v-else-if="props.column.field === 'status'">
              <b-badge
                :variant="getStatusVariant(props.row.status)"
                pill
              >
                {{ props.row.status }}
              </b-badge>
            </span>

            <!-- Date Posted Column -->
            <span v-else-if="props.column.field === 'datePosted'">
              {{ $formatDate(props.row.datePosted) }}
            </span>

            <!-- Actions Column -->
            <span v-else-if="props.column.field === 'actions'">
              <div class="d-flex align-items-center">
                <b-button
                  v-if="$store.state.auth.ActiveUser.company_uen_no == props.row.company_uen_no"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  :variant="props.row.isSaved ? 'flat-warning' : 'flat-secondary'"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="viewCandidates(props.row)"
                >
                  <feather-icon :icon="'EyeIcon'" />
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="flat-info"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="viewJobDetails(props.row)"
                >
                  <feather-icon icon="EyeIcon" />
                </b-button>

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  :variant="props.row.isSaved ? 'flat-warning' : 'flat-secondary'"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="toggleSaveJob(props.row)"
                >
                  <feather-icon :icon="props.row.isSaved ? 'StarIcon' : 'StarIcon'" />
                </b-button>

                <b-button
                  v-if="$store.state.auth.ActiveUser.company_uen_no != props.row.company_uen_no"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon"
                  @click="expressInterest(props.row)"
                >
                  <feather-icon icon="SendIcon" />
                </b-button>
              </div>
            </span>

            <!-- Default Column -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-card>
    </b-overlay>

    <!-- Express Interest Modal -->
    <b-modal
      v-model="showInterestModal"
      title="Express Interest"
      hide-footer
      centered
    >
      <b-form @submit.prevent="submitInterest">
        <b-form-group
          label="Message to Agency"
          label-for="interest-message"
        >
          <b-form-textarea
            id="interest-message"
            v-model="interestMessage"
            placeholder="Introduce yourself and explain why you're interested in this opportunity..."
            rows="4"
            required
          />
        </b-form-group>
        <b-form-group
          label="Attach Candidates"
          label-for="attach-candidates"
        >
          <BotUserSearch
            id="user-name"
            ref="refBotUserIdSelect"
            v-model="selectedCandidates"
            name="user-name"
            :multiple="false"
            @user-selected="checkMatchRate"
          />
          <b-form-checkbox-group
            v-if="false"
            id="attach-candidates"
            v-model="selectedCandidates"
            :options="candidateOptions"
            stacked
          />
        </b-form-group>

        <div v-if="$isNotEmpty(applicantMatchRate)" class="d-flex justify-content-between align-items-center">
          <b-progress
            class="flex-fill"
            max="100"
          >
            <b-progress-bar
              :value="applicantMatchRate.final_score.total * 100"
              :label="`${(applicantMatchRate.final_score.total * 100).toFixed(1)}%`"
              :style="`background-color: ${$getProgressColor(applicantMatchRate.final_score.total * 100)};`"
            />
          </b-progress>
          <span>
            {{ (applicantMatchRate.final_score.total * 100).toFixed(2) }} %
          </span>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            class="mr-1"
            @click="showInterestModal = false"
          >
            Cancel
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
          >
            Send Interest
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Popup: View Candidates -->
    <b-modal
      v-model="popupViewCandidates.state"
      title="List of Candidates"
      hide-footer
      centered
      size="xl"
    >
      <div>
        <div>
          <h4>Interests</h4>
          <InterestTable :id="popupViewCandidates.id"/>
        </div>
  
        <div class="mt-1">
          <div class="d-flex flex-row justify-content-between">
            <h4>Matches</h4>
            <b-button
              variant="primary"
              class="d-flex flex-row align-items-center"
              @click="refreshMatch"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50"
              />
              Refresh Matche List
            </b-button>
          </div>
          <MatchesTable
            v-if="popupViewCandidates.data.matches.state"
            :id="popupViewCandidates.id"
            :refresh="popupViewCandidates.data.matches.refresh"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BBadge,
  BOverlay,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormTextarea,
  BFormCheckboxGroup,
  BCollapse,
  BModal,
  BForm,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import BotUserSearch from "@/components/BotUserSearch.vue"

import InterestTable from "./components/InterestTable.vue"
import MatchesTable from "./components/MatchesTable.vue"

export default {
  name: 'BrokerList',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BBadge,
    BOverlay,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormTextarea,
    BFormCheckboxGroup,
    BCollapse,
    BModal,
    BForm,
    VueGoodTable,

    BotUserSearch,

    InterestTable,
    MatchesTable,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      // Loading state
      isLoading: false,

      // Statistics
      totalBrokerJobs: 0,
      activeJobs: 0,
      matchedCandidates: 0,
      savedJobs: 0,

      // Table data
      brokerJobs: [],
      totalRecords: 0,
      serverParams: {
        columnFilters: { 'company_uen': null },
        sort: {
          field: 'datePosted',
          type: 'desc',
        },
        page: 1,
        perPage: 10,
      },

      // Filters
      showFilters: false,
      filters: {
        status: '',
        agency: '',
        salaryRange: '',
        datePosted: '',
      },

      // Filter options
      statusOptions: [
        { value: '', text: 'All Statuses' },
        { value: 'active', text: 'Active' },
        { value: 'filled', text: 'Filled' },
        { value: 'closed', text: 'Closed' },
      ],
      salaryRangeOptions: [
        { value: '', text: 'All Ranges' },
        { value: '0-2000', text: 'Below $2,000' },
        { value: '2000-4000', text: '$2,000 - $4,000' },
        { value: '4000-6000', text: '$4,000 - $6,000' },
        { value: '6000-8000', text: '$6,000 - $8,000' },
        { value: '8000+', text: 'Above $8,000' },
      ],
      datePostedOptions: [
        { value: '', text: 'All Time' },
        { value: 'today', text: 'Today' },
        { value: 'week', text: 'Past Week' },
        { value: 'month', text: 'Past Month' },
        { value: '3months', text: 'Past 3 Months' },
      ],

      // Modal data
      showInterestModal: false,
      selectedJob: null,
      interestMessage: '',
      selectedCandidates: [],
      applicantMatchRate: {},

      candidateOptions: [],

      // Table columns
      brokerColumns: [
        {
          label: 'Position',
          field: 'position',
          filterOptions: {
            enabled: true,
            placeholder: 'Search position...',
            trigger: 'enter',
          },
          sortable: true,
        },
        // {
        //   label: 'Agency',
        //   field: 'agency',
        //   filterOptions: {
        //     enabled: true,
        //     placeholder: 'Search agency...',
        //     trigger: 'enter',
        //   },
        //   sortable: true,
        // },
        {
          label: 'Company Name',
          field: 'company_name',
          filterOptions: {
            enabled: true,
            placeholder: 'Search company...',
            trigger: 'enter',
          },
          sortable: true,
        },
        {
          label: 'Location',
          field: 'location',
          filterOptions: {
            enabled: true,
            placeholder: 'Search location...',
            trigger: 'enter',
          },
          sortable: true,
        },
        {
          label: 'Salary Range',
          field: 'salary',
          sortable: false,
        },
        {
          label: 'Candidates',
          field: 'candidates',
          sortable: false,
          filter: false
        },
        {
          label: 'Status',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter status...',
            trigger: 'enter',
          },
          sortable: true,
        },
        {
          label: 'Date Posted',
          field: 'datePosted',
          sortable: true,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
        },
      ],

      popupViewCandidates: {
        state: false,
        data: {
          interests: {state: true},
          matches: {state: true},
        },
      },

      interestColumns: [
        {
          label: 'Company Name',
          field: 'company_name',
          filterOptions: {
            enabled: false,
            placeholder: 'Search company...',
            trigger: 'enter',
          },
          sortable: false,
        },
        {
          label: 'Message',
          field: 'message',
          sortable: false,
        },
        {
          label: 'Candidate Name',
          field: 'Candidate.fullname',
          filterOptions: {
            enabled: false,
            trigger: 'enter',
          },
          sortable: false,
        },
        {
          label: 'Match Score',
          field: 'match_score',
          filterOptions: {
            enabled: false,
            trigger: 'enter',
          },
          sortable: false,
        },
        {
          label: 'Candidates',
          field: 'candidates',
          sortable: false,
          filter: false
        },
        {
          label: 'Date Posted',
          field: 'datePosted',
          sortable: true,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
        },
      ],
    }
  },
  mounted() {
    this.loadBrokerJobs()
    this.loadStatistics()
  },
  methods: {
    createNewJob() {
      this.$router.push({ name: 'broker-create' })
    },

    async loadBrokerJobs() {
      this.isLoading = true
      try {
        const params = JSON.stringify({
          page: this.serverParams.page,
          perPage: this.serverParams.perPage,
          sortField: this.serverParams.sort.field,
          sortType: this.serverParams.sort.type,
          columnFilters: this.serverParams.columnFilters,
        })

        const response = await this.$http.get('/api/broker/jobs', { params: { params } })

        if (response.data.success) {
          this.brokerJobs = response.data.data.jobs.map(job => ({
            id: job.id,
            job_id: job.job_id,
            position: job.position,
            company: job.company?.name || 'N/A',
            agency: job.agency?.name || '-',
            company_uen_no: job.company_uen_no || '-',
            company_name: job.company_name || '-',
            agencyLogo: null,
            location: job.working_location_state || 'Singapore',
            salaryMin: job.min_salary || 0,
            salaryMax: job.max_salary || 0,
            candidates: {
              interest_count: job.interest_count || 0,
              matched_count: job.matched_count || 0,
            },
            status: job.status,
            datePosted: job.date_posted,
            isSaved: job.is_saved || false,
          }))
          this.totalRecords = response.data.data.total
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to load broker jobs', {
          showToast: true,
        })
      } finally {
        this.isLoading = false
      }
    },

    async loadStatistics() {
      try {
        const response = await this.$http.get('/api/broker/statistics')

        if (response.data.success) {
          this.totalBrokerJobs = response.data.data.totalJobs
          this.activeJobs = response.data.data.activeJobs
          this.matchedCandidates = response.data.data.matchedCandidates
          this.savedJobs = response.data.data.savedJobs
        }
      } catch (error) {
        console.error('Failed to load statistics:', error)
      }
    },

    // Table event handlers
    onPageChange(params) {
      this.serverParams.page = params.currentPage
      this.loadBrokerJobs()
    },

    onSortChange(params) {
      this.serverParams.sort = {
        field: params[0].field,
        type: params[0].type,
      }
      this.loadBrokerJobs()
    },

    onColumnFilter(params) {
      this.serverParams.columnFilters = params.columnFilters
      this.loadBrokerJobs()
    },

    onPerPageChange(params) {
      this.serverParams.perPage = params.currentPerPage
      this.loadBrokerJobs()
    },

    onSelectionChange(params) {
      // console.log('Selected rows:', params.selectedRows)
    },

    filterJobSource(val) {
      if (this.$isEmpty(val)) {
        delete this.serverParams.columnFilters['company_uen_no']
      } else {
        this.serverParams.columnFilters['company_uen_no'] = val
      }
      this.loadBrokerJobs()
    },

    // Filter methods
    toggleFilters() {
      this.showFilters = !this.showFilters
    },

    applyFilters() {
      this.serverParams.page = 1
      this.loadBrokerJobs()
    },

    clearFilters() {
      this.filters = {
        status: '',
        agency: '',
        salaryRange: '',
        datePosted: '',
      }
      this.applyFilters()
    },

    // Action methods
    refreshJobs() {
      this.loadBrokerJobs()
      this.loadStatistics()
    },

    viewCandidates(row) {
      this.popupViewCandidates.state = true
      this.popupViewCandidates.id = row.id
      this.popupViewCandidates.data.interests = {state: true}
      this.popupViewCandidates.data.matches = {state: true}
    },
    refreshMatch() {
      this.popupViewCandidates.data.matches = {state: false, refresh: true}
      setTimeout(() => {
        this.popupViewCandidates.data.matches = {state: true, refresh: true}
      }, 500)

      setTimeout(() => {
        this.popupViewCandidates.data.matches = {state: true, refresh: false}
      }, 700)
    },

    viewJobDetails(row) {
      this.$router.push({
        name: 'broker-details',
        params: { job_id: row.id },
      })
    },

    async toggleSaveJob(row) {
      try {
        const response = await this.$http.post(`/api/broker/jobs/${row.id}/save`)

        if (response.data.success) {
          row.isSaved = response.data.data.is_saved
          this.$toastSuccess('Success', row.isSaved ? 'Job saved' : 'Job unsaved')

          if (row.isSaved) {
            this.savedJobs++
          } else {
            this.savedJobs--
          }
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to save job', {
          showToast: true,
        })
      }
    },

    expressInterest(row) {
      this.selectedJob = row
      this.interestMessage = ''
      this.selectedCandidates = []
      this.candidateOptions = []
      this.showInterestModal = true
    },

    async submitInterest() {
      try {
        const response = await this.$http.post(`/api/broker/jobs/${this.selectedJob.id}/interest`, {
          message: this.interestMessage,
          candidates: this.selectedCandidates,
        })

        if (response.data.success) {
          this.$toastSuccess('Success', 'Your interest has been sent to the agency')
          this.showInterestModal = false
        }
      } catch (error) {
        this.$handleErrorResponse(error, 'Error', 'Failed to submit interest', {
          showToast: true,
        })
      }
    },

    // Helper methods
    getStatusVariant(status) {
      const variants = {
        active: 'light-success',
        filled: 'light-info',
        closed: 'light-secondary',
      }
      return variants[status] || 'light-secondary'
    },

    checkMatchRate(event) {
      this.selectedCandidates = event

      if (this.$isNotEmpty(this.selectedCandidates)) {
        this.$http
          .post(
            '/api/check-applicant-match-rate',
            {
              job_id: this.selectedJob.job_id,
              applicant_id: this.selectedCandidates.id,
            }
          )
          .then(response => {
            const { success, output } = response.data
  
            if (success) {
              this.applicantMatchRate = output
            }
          })
          .catch(error => {
            //
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.statistics-body {
  padding: 2rem;
}
</style>
